/**
 * Tool for translating image(s) in layer
 */
import ImageTransform from "ui/canvas/ImageTransform";

export default class CanvasTranslate {
	/**
	 * Class constructor
	 *
	 * @param {DataSet} dataSet
	 * @param {number} sceneWidth
	 * @param {number} sceneHeight
	 * @param {number} [startX]
	 * @param {number} [startY]
	 */
	constructor(dataSet, sceneWidth, sceneHeight, startX = 0, startY = 0) {
		/**
		 * @type {DataSet}
		 * @private
		 */
		this._dataSet = dataSet;

		/**
		 * @type {number}
		 * @private
		 */
		this._sceneWidth = sceneWidth;

		/**
		 * @type {number}
		 * @private
		 */
		this._sceneHeight = sceneHeight;

		/**
		 * @type {T_Point}
		 * @private
		 */
		this._startPos = {x: startX, y: startY};

		/**
		 * @type {T_Point}
		 * @private
		 */
		this._shift = {x: 0, y: 0};
	}

	/**
	 * Translates to specified X and Y
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {CanvasTranslate}
	 */
	translateTo(x, y) {
		this._shift.x = x - this._startPos.x;
		this._shift.y = y - this._startPos.y;
		return this;
	}

	/**
	 * Applies translate for rendering canvas
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @returns {CanvasTranslate}
	 */
	applyForContext(ctx) {
		ctx.translate(Math.round(this._shift.x), Math.round(this._shift.y));
		return this;
	}

	/**
	 * Applies translation to the layer
	 *
	 * @param {CanvasLayer} layer
	 * @param {number} [frameFrom]
	 * @param {number} [frameTo]
	 * @returns {Promise<void>}
	 */
	async applyForLayer(layer, frameFrom = -1, frameTo = -1) {
		if (frameFrom === -1) {
			frameFrom = layer.getFrame();
		}

		if (frameTo === -1) {
			frameTo = layer.getFrame();
		}

		if (layer.isReference) {
			let t = new ImageTransform();
			t.translateX = Math.round(this._shift.x);
			t.translateY = Math.round(this._shift.y);
			layer.updateTransformForFrames(t, frameFrom, frameTo);
			return;
		}

		let canvas = document.createElement('canvas');
		canvas.width = this._sceneWidth;
		canvas.height = this._sceneHeight;
		let ctx = canvas.getContext('2d');
		return await layer.updateFrameImages(this._processImage.bind(this, canvas, ctx), frameFrom, frameTo);
		// todo: may be we need some memory clear here, after processing?
	}

	/**
	 * Translates one image
	 *
	 * @param {HTMLCanvasElement} canvas
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {HTMLImageElement} img
	 * @returns {T_DataSetItem}
	 * @private
	 */
	_processImage(canvas, ctx, img) {
		ctx.clearRect(0, 0, this._sceneWidth, this._sceneHeight);
		ctx.save();
		this.applyForContext(ctx);
		ctx.drawImage(img, 0, 0);
		ctx.restore();
		return this._dataSet.setData(canvas.toDataURL('image/png'));
	}
}
